import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import type { PulseReport } from '../../types/pulseReport'
import { PULSE_REPORT_KIND_LABEL } from '../../types/pulseReport'
import { PulseBody } from './PulseBody'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'

const formatUpdatedAt = (updatedAtMs?: number) => {
  if (!updatedAtMs) return 'Update time unavailable'
  const updatedAt = new Date(updatedAtMs)
  if (Number.isNaN(updatedAt.getTime())) return 'Update time unavailable'
  return `Updated ${updatedAt.toLocaleString()}`
}

const downloadMarkdown = (report: PulseReport) => {
  if (typeof document === 'undefined') return
  const blob = new Blob([report.content.narrativeMarkdown], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${PULSE_REPORT_KIND_LABEL[report.kind].toLowerCase()}-pulse-report.md`
  anchor.click()
  URL.revokeObjectURL(url)
}

export const PulseReportReader = ({
  report,
  onClose,
  onRetry,
}: {
  report: PulseReport | null
  onClose: () => void
  onRetry?: (report: PulseReport) => void
}) => {
  if (!report) return null
  const hasContent = Boolean(report.content.narrativeMarkdown.trim())
  const isQueued = report.status === 'PENDING'
  const isFailed = report.status === 'FAILED'
  const copy = () => void navigator.clipboard?.writeText(report.content.narrativeMarkdown)
  return (
    <div
      className="pulseReportReader"
      // eslint-disable-next-line no-restricted-syntax -- full-screen report reader surface
      role="dialog"
      aria-modal="true"
      aria-label="Pulse report reader"
    >
      <header className="pulseReportReader__header">
        <div>
          <span>{PULSE_REPORT_KIND_LABEL[report.kind]}</span>
          <h3>{report.content.headline}</h3>
          <small>{formatUpdatedAt(report.updatedAtMs ?? report.createdAtMs)}</small>
        </div>
        <IconButton
          size="sm"
          variant="tertiary"
          onClick={onClose}
          label="Close report"
          icon={<CloseRoundedIcon fontSize="inherit" />}
        />
      </header>
      <div className="pulseReportReader__body">
        {isQueued ? (
          <div className="pulseReportReader__state" role="status">
            <h4>This report is queued for retry</h4>
            <p>Reconnect to the internet and keep this tab open. Pulse will retry it automatically.</p>
          </div>
        ) : isFailed ? (
          <div className="pulseReportReader__state pulseReportReader__state--failed" role="alert">
            <h4>Report failed to generate</h4>
            <p>{report.error?.trim() || 'An unknown error prevented this report from being generated.'}</p>
            {onRetry ? (
              <Button size="md" variant="secondary" onClick={() => onRetry(report)}>
                Retry
              </Button>
            ) : null}
          </div>
        ) : !hasContent ? (
          <div className="pulseReportReader__state" role="status">
            <h4>This report has no content</h4>
            <p>Retry generation to create the report.</p>
            {onRetry ? (
              <Button size="md" variant="secondary" onClick={() => onRetry(report)}>
                Retry
              </Button>
            ) : null}
          </div>
        ) : <PulseBody report={report} />}
      </div>
      {hasContent && !isQueued && !isFailed ? (
        <footer className="pulseReportReader__footer">
          <Button size="md" variant="secondary" onClick={copy} icon={<ContentCopyRoundedIcon fontSize="small" />}>
            Copy Markdown
          </Button>
          <Button size="md" variant="secondary" onClick={() => downloadMarkdown(report)} icon={<DownloadRoundedIcon fontSize="small" />}>
            Download MD
          </Button>
        </footer>
      ) : <footer className="pulseReportReader__footer" />}
    </div>
  )
}
